import { FC, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { withdrawalSelector } from '@/core/services/Wallets/WalletSelector'
import {
  getWalletWithdrawalThunk,
  withdrawalThunk,
} from '@/core/services/Wallets/WalletSlice'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import { cn } from '@bem-react/classname'
import { yupResolver } from '@hookform/resolvers/yup'

import { useHandleServerError } from '@/hooks/useHandleServerError'

import { Checkbox } from '@/components/Form/Checkbox'
import { Field } from '@/components/Form/Field'
import { Back } from '@/components/UI/Back'
import { Button } from '@/components/UI/Button'
import Icon from '@/components/UI/Icon'
import { Loading } from '@/components/UI/Loading'
import { Modal } from '@/components/UI/Modal'
import { Title } from '@/components/UI/Title'

import yup from '@/utils/yup-extended'

import {
  WalletWithdrawalActionType,
  WalletWithdrawalModel,
} from './models/WalletsModel'
import './styles/WalletSend.scss'

const cnWalletSend = cn('WalletSend')

export const WalletSend: FC = () => {
  const { t } = useTranslation()
  const { code } = useParams<{ code: string }>()
  const dispatch = useAppDispatch()
  const { handleServerError } = useHandleServerError()

  const withdrawal = useAppSelector(withdrawalSelector)

  const [isLoading, setLoading] = useState<boolean>(true)
  const [isOpenModal, setOpenModal] = useState<boolean>(false)
  const [isChecked, setIsChecked] = useState<boolean>(false)
  const validationSchema = yup.object().shape({
    amount: yup
      .number()
      .transform(value => (isNaN(value) ? undefined : value))
      .required(t('withdraw-amount-required'))
      .test('min-amount', t('withdraw-amount-min'), value => {
        if (!value) return false
        return value > 0
      })
      .test('max-amount', t('withdraw-amount-max'), value => {
        if (!value || !withdrawal || !withdrawal.max_amount) return true
        const max = parseFloat(withdrawal.max_amount)
        return value <= max
      }),
    address: yup
      .string()
      .required(t('withdraw-address-required'))
      .min(10, t('withdraw-address-min-length')),
    action: yup
      .string()
      .oneOf([WalletWithdrawalActionType.SEND_ACTION])
      .default(WalletWithdrawalActionType.SEND_ACTION),

    code: yup.string().required(t('withdraw-code-required')),
  })

  useEffect(() => {
    if (code) getWalletSend(code)
  }, [code])

  const getWalletSend = async (code: string) => {
    try {
      await dispatch(getWalletWithdrawalThunk(code)).unwrap()

      setLoading(false)
    } catch (error) {
      handleServerError(error)
      setLoading(false)
    }
  }

  useEffect(() => {
    if (code) setValue('code', code.toUpperCase())
  }, [code])

  const preSubmit = async () => {
    setOpenModal(true)
  }

  const onSubmit = async () => {
    const data: WalletWithdrawalModel = getValues()
    try {
      await dispatch(withdrawalThunk(data)).unwrap()
      setLoading(false)
      setOpenModal(false)
      setValue('amount', 0)
      setValue('address', '')
      toast.success(t('withdraw-success'))

      if (code) await getWalletSend(code)
    } catch (error) {
      handleServerError(error)
      setLoading(false)
    }
  }

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<WalletWithdrawalModel>({
    defaultValues: new WalletWithdrawalModel(),
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  })

  return (
    <>
      {isLoading ? (
        <Loading type='circle' minHeight='300px' />
      ) : (
        <form className={cnWalletSend()} onSubmit={handleSubmit(preSubmit)}>
          {withdrawal && code && (
            <>
              <div className={cnWalletSend('top')}>
                <Back />
                <Title>
                  {t('withdraw-title', { code: code.toUpperCase() })}
                </Title>
              </div>

              <div className={cnWalletSend('wrap')}>
                <Field
                  {...register('code')}
                  label={t('withdraw-cryptocurrency')}
                  disabled
                  mb='none'
                />

                <div className={cnWalletSend('group')}>
                  <Field
                    {...register('amount')}
                    label={t('withdraw-amount')}
                    mb='none'
                    required
                    errors={errors.amount?.message}
                    onFocus={e => {
                      if (e.target.value === '0') {
                        e.target.value = ''
                        setValue('amount', 0)
                      }
                    }}
                  />

                  <div className={cnWalletSend('card')}>
                    <div className={cnWalletSend('info')}>
                      <div className={cnWalletSend('balance')}>
                        {+withdrawal.balance + ' ' + withdrawal.code}
                      </div>
                      <div className={cnWalletSend('label')}>
                        {t('withdraw-balance')}
                      </div>
                    </div>
                    <Button
                      onClick={() => {
                        if (withdrawal?.max_amount) {
                          setValue('amount', parseFloat(withdrawal.max_amount))
                        }
                      }}
                    >
                      {t('withdraw-all')}
                    </Button>
                  </div>
                </div>

                <Field
                  {...register('address')}
                  label={t('withdraw-address', { address: withdrawal.code })}
                  mb='none'
                  required
                  errors={errors.address?.message}
                />

                <div className={cnWalletSend('percent')}>
                  <div className={cnWalletSend('icon')}>
                    <Icon name='percent' />
                  </div>

                  {withdrawal.fee && (
                    <div className={cnWalletSend('item')}>
                      <div className={cnWalletSend('fee')}>
                        {+withdrawal.fee.replaceAll('~', '') +
                          ' ' +
                          withdrawal.fee_code}
                      </div>
                      <div className={cnWalletSend('label')}>
                        {t('withdraw-network')}
                      </div>
                    </div>
                  )}
                </div>

                <Button type='submit'>{t('withdraw-continue')}</Button>
              </div>
            </>
          )}
        </form>
      )}

      <Modal
        type='error'
        open={isOpenModal}
        onClose={() => setOpenModal(false)}
        styles={{ maxWidth: 350 }}
        className={cnWalletSend('modal')}
      >
        <div className={cnWalletSend('svg')}>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
          >
            <path
              d='M5 19L19 5M9 7C9 8.10457 8.10457 9 7 9C5.89543 9 5 8.10457 5 7C5 5.89543 5.89543 5 7 5C8.10457 5 9 5.89543 9 7ZM19 17C19 18.1046 18.1046 19 17 19C15.8954 19 15 18.1046 15 17C15 15.8954 15.8954 15 17 15C18.1046 15 19 15.8954 19 17Z'
              stroke='#FF6A38'
              stroke-width='1.5'
              stroke-linecap='round'
              stroke-linejoin='round'
            />
          </svg>
        </div>

        <div className={cnWalletSend('subtitle')}>
          Warning of a possible risk
        </div>
        <div className={cnWalletSend('desc')}>
          Make sure you select the correct withdrawal address. Once a
          transaction is sent to the blockchain, we cannot undo it.
        </div>

        <div className={cnWalletSend('text')}>
          Beware of scams, such as low prices on prepaid virtual gift cards or
          asking you to deposit funds on unknown platforms.
          <br />
          <br />
          ExStream will never ask you to withdraw funds to another address.
        </div>

        <Checkbox
          label='I am aware of all the risks and want to continue withdrawing funds'
          checked={isChecked}
          onChange={() => setIsChecked(!isChecked)}
          id={'aware'}
          name={'aware'}
        />

        <div className={cnWalletSend('btns')}>
          <Button disabled={!isChecked} onClick={() => onSubmit()}>
            Continue
          </Button>
          <Button color='light' onClick={() => setOpenModal(false)}>
            Do not send
          </Button>
        </div>
      </Modal>
    </>
  )
}
